import { Dispatch, SetStateAction, useState } from "react";
import TextField from "@mui/material/TextField";
import CloseIcon from "@mui/icons-material/Close";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import {
    Modal,
    Button,
    FormControl,
    Select,
    MenuItem,
    Checkbox,
    FormControlLabel,
    InputLabel,
    IconButton,
    Box,
    Typography,
    SelectChangeEvent,
    OutlinedInput,
    ListItemText
} from "@mui/material";

interface ModalProps {
    open: boolean;
    onClose: () => void;
    setOpen: Dispatch<SetStateAction<boolean>>;
}

interface Condition {
    id: string;
    field: string;
    operator: string;
    value: string;
}

export default function CreateSegment({
    open,
    onClose,
    setOpen,
}: ModalProps) {
    const [segmentName, setSegmentName] = useState("");
    const [segmentType, setSegmentType] = useState("Static");
    const [segmentPurpose, setSegmentPurpose] = useState("");
    const [description, setDescription] = useState("");
    const [conditions, setConditions] = useState<Condition[]>([]);
    const [useAISuggest, setUseAISuggest] = useState(false);
    const [selectedUsers, setSelectedUsers] = useState<string[]>([]);

    const userOptions = [
        "Vendor A",
        "Vendor B",
        "Vendor C",
        "Vendor D",
        "Vendor E",
    ];

    const handleUserChange = (event: SelectChangeEvent<typeof selectedUsers>) => {
        const {
            target: { value },
        } = event;
        setSelectedUsers(
            typeof value === "string" ? value.split(",") : value
        );
    };

    const fieldOptions = [
        "Revenue",
        "Orders",
        "Health Score",
        "Country",
        "Category",
        "Refund Rate",
        "SLA Breach Count",
        "Conversion Rate",
        "Cart Drop Rate",
        "Sell-Through Rate",
        "Checkout Completion Rate",
        "GMV",
        "Margin",
        "Rating",
        "Cancellation Rate",
        "Dispute Rate",
    ];

    const operatorOptions: Record<string, string[]> = {
        numeric: [">", ">=", "<", "<=", "=", "!="],
        percentage: [">", ">=", "<", "<=", "=", "!="],
        text: ["=", "!=", "contains", "starts with", "ends with"],
    };

    const getOperatorOptions = (field: string) => {
        if (
            field === "Country" ||
            field === "Category"
        ) {
            return operatorOptions.text;
        }
        if (
            field.includes("Rate") ||
            field.includes("Percentage")
        ) {
            return operatorOptions.percentage;
        }
        return operatorOptions.numeric;
    };

    const addCondition = () => {
        const newCondition: Condition = {
            id: Date.now().toString(),
            field: fieldOptions[0],
            operator: ">",
            value: "",
        };
        setConditions([...conditions, newCondition]);
    };

    const removeCondition = (id: string) => {
        setConditions(conditions.filter((c) => c.id !== id));
    };

    const updateCondition = (
        id: string,
        field: keyof Condition,
        value: string
    ) => {
        setConditions(
            conditions.map((c) => {
                if (c.id === id) {
                    const updated = { ...c, [field]: value };
                    // Reset operator if field changes
                    if (field === "field") {
                        updated.operator = getOperatorOptions(value)[0];
                    }
                    return updated;
                }
                return c;
            })
        );
    };

    const handleAISuggest = () => {
        setUseAISuggest(true);
        // In a real app, this would call an AI service
        // For now, we'll just show a message
        alert(
            "AI Suggestion: Based on your data, we suggest creating a segment for 'High-Value Vendors' with Revenue > 50000 and Health Score >= 80"
        );
    };

    const handleSubmit = () => {
        // Validation
        if (!segmentName.trim()) {
            alert("Segment Name is required");
            return;
        }
        if (!segmentType) {
            alert("Segment Type is required");
            return;
        }
        if (!segmentPurpose) {
            alert("Segment Purpose is required");
            return;
        }
        if (conditions.length === 0) {
            alert("At least one condition is required");
            return;
        }

        // In a real app, this would submit to the backend
        console.log({
            segmentName,
            segmentType,
            segmentPurpose,
            description,
            conditions,
            useAISuggest,
            selectedUsers,
        });

        // Reset form
        setSegmentName("");
        setSegmentType("Static");
        setSegmentPurpose("");
        setDescription("");
        setConditions([]);
        setUseAISuggest(false);
        setSelectedUsers([]);
        setOpen(false);
    };

    return (
        <Modal
            className="modal rejectReason_modal"
            open={open}
            onClose={onClose}
        >
            <div className="modal-dialog take_action_modal" style={{ maxWidth: "800px" }}>
                <div className="modal-body">

                    {/* Header */}
                    <div className="modal_title">
                        <h2>Create Segment</h2>
                        <button
                            type="button"
                            className="btn-close"
                            onClick={() => setOpen(false)}
                        >
                            <CloseIcon />
                        </button>
                    </div>

                    {/* Form */}
                    <form className="form">
                        <div className="gap_p">

                            {/* AI Suggest Segment Button */}
                            <div className="control_group">
                                <Button
                                    className="btn btn_secondary"
                                    startIcon={<SmartToyOutlinedIcon />}
                                    onClick={handleAISuggest}
                                    fullWidth
                                >
                                    AI Suggest Segment
                                </Button>
                            </div>

                            {/* Segment Name - REQUIRED */}
                            <div className="control_group">
                                <label htmlFor="segment-name">
                                    Segment Name <span style={{ color: "red" }}>*</span>
                                </label>
                                <TextField
                                    placeholder="e.g. High-Value Vendors"
                                    fullWidth
                                    hiddenLabel
                                    value={segmentName}
                                    onChange={(e) => setSegmentName(e.target.value)}
                                    required
                                    inputProps={{ id: "segment-name" }}
                                />
                            </div>

                            {/* Segment Type - REQUIRED */}
                            <div className="control_group">
                                <label htmlFor="segment-type">
                                    Segment Type <span style={{ color: "red" }}>*</span>
                                </label>
                                <FormControl fullWidth>
                                    <Select
                                        value={segmentType}
                                        onChange={(e) => setSegmentType(e.target.value)}
                                        required
                                        inputProps={{ id: "segment-type" }}
                                    >
                                        <MenuItem value="Static">Static</MenuItem>
                                        <MenuItem value="Dynamic">Dynamic</MenuItem>
                                        <MenuItem value="Predictive">Predictive (AI)</MenuItem>
                                    </Select>
                                </FormControl>
                            </div>

                            {/* Static Segment Users - REQUIRED IF STATIC */}
                            {segmentType === "Static" && (
                                <div className="control_group">
                                    <label htmlFor="segment-users">
                                        Select Users <span style={{ color: "red" }}>*</span>
                                    </label>
                                    <FormControl fullWidth>
                                        <Select
                                            multiple
                                            value={selectedUsers}
                                            onChange={handleUserChange}
                                            input={<OutlinedInput id="segment-users" />}
                                            renderValue={(selected) => selected.join(", ")}
                                        >
                                            {userOptions.map((name) => (
                                                <MenuItem key={name} value={name}>
                                                    <Checkbox checked={selectedUsers.indexOf(name) > -1} />
                                                    <ListItemText primary={name} />
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </div>
                            )}

                            {/* Segment Purpose - REQUIRED */}
                            <div className="control_group">
                                <label htmlFor="segment-purpose">
                                    Segment Purpose <span style={{ color: "red" }}>*</span>
                                </label>
                                <FormControl fullWidth>
                                    <Select
                                        value={segmentPurpose}
                                        onChange={(e) => setSegmentPurpose(e.target.value)}
                                        required
                                        inputProps={{ id: "segment-purpose" }}
                                    >
                                        <MenuItem value="">Select Purpose</MenuItem>
                                        <MenuItem value="Analytics">Analytics</MenuItem>
                                        <MenuItem value="Incentives">Incentives</MenuItem>
                                        <MenuItem value="Risk Monitoring">Risk Monitoring</MenuItem>
                                        <MenuItem value="Communication">Communication</MenuItem>
                                    </Select>
                                </FormControl>
                            </div>

                            {/* Description */}
                            <div className="control_group">
                                <label htmlFor="segment-description">Description (Optional)</label>
                                <TextField
                                    placeholder="Describe this segment..."
                                    fullWidth
                                    multiline
                                    rows={3}
                                    hiddenLabel
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    inputProps={{ id: "segment-description" }}
                                />
                            </div>

                            {/* Condition Builder */}
                            <div className="control_group">
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
                                    <label>
                                        Condition Builder <span style={{ color: "red" }}>*</span>
                                    </label>
                                    <Button
                                        size="small"
                                        startIcon={<AddIcon />}
                                        onClick={addCondition}
                                        className="btn btn_primary"
                                    >
                                        Add Condition
                                    </Button>
                                </div>

                                {conditions.length === 0 ? (
                                    <Box
                                        sx={{
                                            p: 2,
                                            border: "1px dashed #ccc",
                                            borderRadius: 1,
                                            textAlign: "center",
                                            color: "#999",
                                        }}
                                    >
                                        <Typography variant="body2">
                                            No conditions added. Click "Add Condition" to start building rules.
                                        </Typography>
                                    </Box>
                                ) : (
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                        {conditions.map((condition, index) => (
                                            <Box
                                                key={condition.id}
                                                sx={{
                                                    p: 2,
                                                    border: "1px solid #e0e0e0",
                                                    borderRadius: 1,
                                                    display: "flex",
                                                    gap: 2,
                                                    alignItems: "center",
                                                    flexWrap: "wrap",
                                                }}
                                            >
                                                <Typography variant="body2" sx={{ minWidth: "60px" }}>
                                                    And
                                                </Typography>

                                                <FormControl size="small" sx={{ minWidth: "150px" }}>
                                                    <InputLabel>Field</InputLabel>
                                                    <Select
                                                        value={condition.field}
                                                        label="Field"
                                                        onChange={(e) =>
                                                            updateCondition(
                                                                condition.id,
                                                                "field",
                                                                e.target.value
                                                            )
                                                        }
                                                    >
                                                        {fieldOptions.map((field) => (
                                                            <MenuItem key={field} value={field}>
                                                                {field}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>

                                                <FormControl size="small" sx={{ minWidth: "120px" }}>
                                                    <InputLabel>Operator</InputLabel>
                                                    <Select
                                                        value={condition.operator}
                                                        label="Operator"
                                                        onChange={(e) =>
                                                            updateCondition(
                                                                condition.id,
                                                                "operator",
                                                                e.target.value
                                                            )
                                                        }
                                                    >
                                                        {getOperatorOptions(condition.field).map(
                                                            (op) => (
                                                                <MenuItem key={op} value={op}>
                                                                    {op}
                                                                </MenuItem>
                                                            )
                                                        )}
                                                    </Select>
                                                </FormControl>

                                                <TextField
                                                    size="small"
                                                    placeholder="Value"
                                                    value={condition.value}
                                                    onChange={(e) =>
                                                        updateCondition(
                                                            condition.id,
                                                            "value",
                                                            e.target.value
                                                        )
                                                    }
                                                    sx={{ minWidth: "150px", flex: 1 }}
                                                    type={
                                                        condition.field === "Country" ||
                                                        condition.field === "Category"
                                                            ? "text"
                                                            : "number"
                                                    }
                                                />

                                                <IconButton
                                                    color="error"
                                                    onClick={() => removeCondition(condition.id)}
                                                    size="small"
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Box>
                                        ))}
                                    </Box>
                                )}
                            </div>

                        </div>

                        {/* Footer */}
                        <div className="footer_btn_flex">
                            <Button
                                className="btn"
                                onClick={() => {
                                    setSegmentName("");
                                    setSegmentType("Static");
                                    setSegmentPurpose("");
                                    setDescription("");
                                    setConditions([]);
                                    setUseAISuggest(false);
                                    setSelectedUsers([]);
                                    setOpen(false);
                                }}
                            >
                                Cancel
                            </Button>

                            <Button
                                className="btn btn_primary"
                                onClick={handleSubmit}
                            >
                                Create Segment
                            </Button>
                        </div>
                    </form>

                </div>
            </div>
        </Modal>
    );
}
